import { TvShow } from "../types/TvShow";

/*
  Enumerates simple cycles (TV reference paradoxes) in the reference graph.

  This is inherently exponential, so on a dense graph it can find millions of
  cycles and block the UI thread forever. To stay responsive we bound the
  search by:
    - maxCycleLength: only report cycles up to this many shows, and
    - maxCycles: stop once we've collected this many.
  These caps keep the most interesting (shorter) paradoxes while guaranteeing
  the computation terminates quickly. Override via the optional opts arg.
*/
const DEFAULT_MAX_CYCLE_LENGTH = 6;
const DEFAULT_MAX_CYCLES = 2000;

export function GetCycles(
  tvShows: Map<string, TvShow>,
  opts: { maxCycleLength?: number; maxCycles?: number } = {}
) {
  const maxCycleLength = opts.maxCycleLength ?? DEFAULT_MAX_CYCLE_LENGTH;
  const maxCycles = opts.maxCycles ?? DEFAULT_MAX_CYCLES;

  const cycles: string[][] = [];
  const dedupCycles: Set<string> = new Set();
  const visitedSet: Set<string> = new Set();

  function dfsForCycles(title: string, visited: string[]) {
    if (cycles.length >= maxCycles) {
      return;
    }
    // Bound path length (visited includes the start node).
    if (visited.length > maxCycleLength) {
      return;
    }

    const node = tvShows.get(title);
    if (!node) {
      return;
    }
    const referencesTo = Array.from(node.referencesTo.keys());
    if (referencesTo.length === 0) {
      return;
    }

    for (const referenceTitle of referencesTo) {
      if (cycles.length >= maxCycles) {
        return;
      }

      if (referenceTitle === visited[0]) {
        // Found a cycle back to the start. Canonicalize to dedup rotations.
        const canonical = canonicalCycleKey(visited);
        if (!dedupCycles.has(canonical)) {
          dedupCycles.add(canonical);
          cycles.push([...visited, referenceTitle]);
        }
        continue;
      }

      // Skip nodes already on the current path (only the start may close it).
      if (visitedSet.has(referenceTitle)) {
        continue;
      }

      visited.push(referenceTitle);
      visitedSet.add(referenceTitle);
      dfsForCycles(referenceTitle, visited);
      visited.pop();
      visitedSet.delete(referenceTitle);
    }
  }

  const titles = Array.from(tvShows.keys());
  for (const title of titles) {
    if (cycles.length >= maxCycles) {
      break;
    }
    visitedSet.clear();
    visitedSet.add(title);
    dfsForCycles(title, [title]);
  }

  cycles.sort((a, b) => a.length - b.length);

  return cycles;
}

// A cycle is the same regardless of which node we start from. Build a
// rotation-invariant key so 'A->B->C->A' and 'B->C->A->B' dedupe to one.
function canonicalCycleKey(cycleNodes: string[]): string {
  const n = cycleNodes.length;
  let best: string | null = null;
  for (let i = 0; i < n; i++) {
    const rotated = cycleNodes.slice(i).concat(cycleNodes.slice(0, i));
    const key = rotated.join("\u0000");
    if (best === null || key < best) {
      best = key;
    }
  }
  return best ?? "";
}
